<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max flex items-center gap-2">
            <span class="company-name">
              {{ user.businessName || '—' }}
            </span>
            <slot />
            <VaAvatar :size="32" color="warning" class="overflow-hidden" :src="user.logoPath || undefined">
              <span v-if="!user.logoPath" style="font-size: 1rem; color: #fff">
                {{ user.initials }}
              </span>
            </VaAvatar>
          </span>
        </VaButton>
      </template>

      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in filteredOptions" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>

          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="onItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>

          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useUserStore } from '../../../../stores/user'
import { AuthApi } from '../../../../services/auth'
import { stopPresence } from '../../../../services/presence'

const router = useRouter()
const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const { t } = useI18n()
const user = useUserStore()

async function logout() {
  await AuthApi.logout()
  user.clear()
  stopPresence()
  router.push({ name: 'login' })
}

const filteredOptions = computed(() =>
  (mergedOptions.value ?? []).map((g) => ({
    ...g,
    list: g.list.filter((it) => it.name !== 'profile' || user.hasRole('Admin')),
  })),
)

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = defineProps<{
  options?: ProfileOptions[]
}>()

const defaultOptions: ProfileOptions[] = [
  {
    name: 'account',
    separator: true,
    list: [...(user.hasRole('Admin') ? [{ name: 'profile', to: 'preferences', icon: 'mso-account_circle' }] : [])],
  },
  {
    name: '',
    separator: false,
    list: [{ name: 'logout', icon: 'mso-logout', action: logout }],
  },
]

const mergedOptions = computed<ProfileOptions[]>(() => props.options ?? defaultOptions)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) return {}
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const onItemClick = (item: ProfileListItem) => {
  if (item.action) {
    isShown.value = false
    item.action()
  }
}
</script>

<style lang="scss">
.company-name {
  max-width: 180px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
}
@media (max-width: 480px) {
  .company-name {
    display: none;
  }
}
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
